import React, { useCallback, useState } from 'react';

import _ from 'lodash';
import moment from 'moment';
import { Button, IconButton } from '@material-ui/core';
import { MdDelete, MdManageHistory } from 'react-icons/md';

import API from 'utils/api';
import { useSingleton, useSources, useTableStateRestore } from 'hooks';
import { ButtonContainer, ConfirmDialog, ErrorDialog, Table, Tooltip } from 'components';
import { breakSentence, call } from 'utils';

import styles from './DrawList.module.scss';

const DrawList = props => {
  const [dialog, setDialog] = useState({});
  const sources = useSources();
  const singleton = useSingleton();

  const { tableProps, forwardLink } = useTableStateRestore(props);

  const fetchData = useCallback(
    ({ filter, ...rest }) =>
      API.getDraws(
        {
          ...rest,
          query: filter
            ? JSON.stringify(
                _.mapValues(filter, (value, key) =>
                  key === 'description' ? { $regex: value, $options: 'i' } : value
                )
              )
            : undefined,
          select: 'description updatedAt status date image published',
        },
        sources
      ),
    [sources]
  );

  const renderActions = ({ _id: id, description, published }) => {
    const button = (Icon, onClick, title, disabled) => (
      <IconButton color="primary" onClick={onClick} disabled={disabled}>
        <Tooltip title={title} placement="top">
          <Icon />
        </Tooltip>
      </IconButton>
    );

    return (
      <div>
        {button(
          MdManageHistory,
          () => props.history.push(forwardLink(`manager/${id}`)),
          'Manage this draw'
        )}

        {button(
          MdDelete,
          () =>
            setDialog({
              delete: {
                id,
                message: [
                  'Are you sure you want to delete this draw?',
                  `"${_.truncate(description, { length: 100 })}"`,
                ],
              },
            }),
          'Delete this draw',
          published
        )}
      </div>
    );
  };

  const renderDescription = ({ image, description }) => {
    const url = _.get(image, 'url');

    return (
      <div className={styles.description}>
        <div
          className={styles.icon}
          style={{ backgroundImage: url ? `url(${url})` : null }}
        />
        <div className={styles.text}>{description}</div>
      </div>
    );
  };

  const onCloseDialog = () => setDialog({});

  return (
    <div className={styles.drawList}>
      <ButtonContainer position="top">
        <Button
          variant="contained"
          color="primary"
          onClick={() =>
            API.createDraw({}, sources).then(({ data, error }) => {
              data
                ? props.history.push(forwardLink(`manager/${data}`))
                : setDialog({ error });
            })
          }
        >
          create new draw
        </Button>
      </ButtonContainer>

      <Table
        {...tableProps}
        data={fetchData}
        title="Draw List"
        filtering
        pageSizeOptions={[5, 10, 20, 40]}
        pageSize={10}
        search={false}
        columns={[
          {
            title: 'Description',
            field: 'description',
            weight: 2,
            render: renderDescription,
          },
          {
            title: 'Last Modified',
            field: 'updatedAt',
            align: 'center',
            initialSort: 'desc',
            defaultSort: 'desc',
            filtering: false,
            render: ({ updatedAt }) => (
              <div>{updatedAt ? moment(updatedAt).format('YYYY/MM/DD hh:mm A') : ''}</div>
            ),
          },
          {
            title: 'Draw Date',
            field: 'date',
            align: 'center',
            initialSort: 'desc',
            filtering: false,
            render: ({ date }) => (
              <div>{date ? moment(date).format('YYYY/MM/DD') : ''}</div>
            ),
          },
          {
            title: 'Status',
            field: 'status',
            align: 'center',
            render: ({ status }) => _.capitalize(status),
            filtering: {
              type: 'select',
              options: ['idle', 'open', 'closed', 'redemption', 'ended'].map(it => ({
                text: _.capitalize(it),
                value: it,
              })),
            },
          },
          {
            title: 'Actions',
            sorting: false,
            align: 'center',
            render: renderActions,
            filtering: false,
          },
        ]}
      />

      <ConfirmDialog
        open={!!dialog.delete}
        title="Delete Draw"
        message={_.get(dialog, 'delete.message', '')}
        busy={_.get(dialog, 'delete.busy')}
        actions={_.get(dialog, 'delete.actions')}
        onClose={yes => {
          if (yes) {
            const data = dialog.delete;
            setDialog({ delete: { ...data, message: 'Deleting draw...', busy: true } });

            singleton(() =>
              API.deleteDraw(data.id, sources).then(({ error }) => {
                if (error) {
                  setDialog({
                    delete: {
                      ...data,
                      message: breakSentence(String(error), 51),
                      actions: 'close',
                    },
                  });
                } else {
                  call(tableProps.tableRef.current.reload);
                  onCloseDialog();
                }
              })
            );
          } else {
            onCloseDialog();
          }
        }}
      />

      <ErrorDialog open={!!dialog.error} onClose={onCloseDialog} message={dialog.error} />
    </div>
  );
};

export default DrawList;
